import { ConfigService, SharedConfigKey } from '@ddt-web/config-layer';
import { SeriesMapper } from '@ddt-web/data-service';
import { PortfolioIdUtil } from '@ddt-web/portfolio-data';
import { chain } from 'lodash';
import * as get from 'lodash/get';
import * as isArray from 'lodash/isArray';

export class CommonPeriodMapper implements SeriesMapper {

	/**
	 * This method can be used to map an object from one store to an object in a different store.
	 * For this config we can use it to map securities in performance debugData to the securities in the portfolio holdings.
	 */
	constructor(
		public readonly storeDependencies: string[],
		public readonly performanceExpression: string,
		public readonly holdingsExpression: string,
		public readonly modelPortfolioChildExpression?: string
	) { }

	public getStoreDependencies(): string[] {
		return this.storeDependencies;
	}
	public mapper(...storeData: any[]): any[] {
		const lookup: { [storeName: string]: any } = {};
		for (let i = 0; i < storeData.length; i++) {
			lookup[this.storeDependencies[i]] = storeData[i];
		}

		// Get holdings
		const holdings = chain(lookup)
			.get(this.holdingsExpression, [])
			.value();

		// Get nested child models if the expression exists
		const childModels = chain(lookup)
			.get(this.modelPortfolioChildExpression, [])
			.map(data => {
				const nestedData = get(data, 'portfolio.children');
				if (nestedData && !PortfolioIdUtil.isModel(get(data, 'portfolio.id'))) {
					if (typeof nestedData === 'object') {
						return Object.keys(nestedData).map(key => nestedData[key]);
					} else {
						return nestedData;
					}
				} else {
					return data;
				}
			})
			.flattenDeep()
			.filter(data => PortfolioIdUtil.isModel(get(data, 'portfolio.id')))
			.value();


		// Get data from performance and return the combined object
		const result = chain(lookup)
			.get(this.performanceExpression, [])
			.flatMapDeep()
			.map(data => {
				const storeObject = lookup['associatedPortfolios'] || Object.values(lookup)?.[0];
				const modelId = storeObject?.aladdinPortfolioCode || storeObject?.tpmId;

				const filtered = [...holdings, ...childModels].find(holding => {
					const modelChildData = holding?.portfolio?.allData || holding?.portfolio; // when coming from PE, there is no allData provided
					let childModelId = modelChildData?.id;
					// for BLK models only, update childModelId to use aladdinPortfolioCode when SHOULD_USE_DAS_PORTFOLIO_ID is false
					if (PortfolioIdUtil.isBlackRockModel(childModelId) && !ConfigService.instance.get(SharedConfigKey.SHOULD_USE_DAS_PORTFOLIO_ID, true)) {
						childModelId = modelChildData?.aladdinPortfolioCode;
					}

					// Get the details if the child is a model portfolio
					if (PortfolioIdUtil.isModel(childModelId)) {
						// We only want to return true if the model is a child
						return childModelId === data.assetId && childModelId !== modelId;
					}
					return holding.requestId === data.assetId;
				});

				// if holding exist in both holding and performance
				// if performance data is for portfolio
				if (filtered || modelId === data.assetId) { return { ...data, ...filtered }; }
			})
			.filter(data => data)
			.value();

		return isArray(result) ? result : [result];
	}
}
